import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  Field,
  Icon,
  Input,
} from "../../../design-system";
import {
  approveWorkdayCorrection,
  cancelWorkdayCorrection,
  rejectWorkdayCorrection,
} from "../../../helpers/kronaly_helper";
import { formatDateOnly, formatInstant, formatTimeHHMM } from "../../../helpers/datetime";

const STATUS_LABEL = {
  pending: { label: "Pendiente", tone: "warning" },
  approved: { label: "Aprobada", tone: "success" },
  rejected: { label: "Rechazada", tone: "danger" },
  cancelled: { label: "Cancelada", tone: "neutral" },
};

function unwrapErr(e) {
  return e?.response?.data?.message || e?.response?.data?.error || e?.message || "Operación fallida.";
}

export function DetalleCorreccionDrawer({
  correction,
  tz,
  currentUserId,
  currentEmployeeProfileId,
  isManager,
  onClose,
  onAfterAction,
}) {
  const open = !!correction;
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    setComment("");
    setError(null);
    setSubmitting(false);
  }, [correction?.id]);

  if (!correction) {
    return <Drawer open={false} onClose={onClose} title={null} />;
  }

  const status = STATUS_LABEL[correction.status] || { label: correction.status, tone: "neutral" };
  const isSelfOrigin = correction.requested_origin === "self";
  const requestedByMe = correction.requested_by === currentUserId;
  const affectsMe = correction.employee_profile_id === currentEmployeeProfileId;

  // ¿Puedo decidir?
  // origin=self → managers (capability).
  // origin=on_behalf → solo el empleado afectado (target).
  const canDecide = correction.status === "pending"
    && (isSelfOrigin ? (isManager && !requestedByMe) : affectsMe);
  // ¿Puedo cancelar? Solo si soy el solicitante y está pendiente.
  const canCancel = correction.status === "pending" && requestedByMe;

  const onAct = async (fn) => {
    setSubmitting(true);
    setError(null);
    try {
      await fn();
      onAfterAction?.();
    } catch (e) {
      setError(unwrapErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = () => onAct(() => approveWorkdayCorrection(correction.id, comment.trim() || null));
  const handleReject = () => onAct(() => rejectWorkdayCorrection(correction.id, comment.trim() || null));
  const handleCancel = () => onAct(() => cancelWorkdayCorrection(correction.id));

  const title = (
    <div>
      <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
        Corrección de jornada
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--kr-fg-strong)", marginTop: 2 }}>
        {formatDateOnly(correction.work_date, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
      </div>
    </div>
  );

  return (
    <Drawer open={open} onClose={onClose} title={title} size="lg">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {error && <Alert tone="danger">{error}</Alert>}

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Badge tone={status.tone} dot>{status.label}</Badge>
          <Badge tone="neutral" dot>
            {isSelfOrigin ? "Auto-corrección" : "Corregida por otro"}
          </Badge>
          {affectsMe && <Badge tone="info" dot>Afecta a ti</Badge>}
        </div>

        <MetaRow
          label="Empleado afectado"
          value={affectsMe ? "Tú" : (correction.employee_name || correction.employee_profile_id)}
        />
        <MetaRow
          label="Solicitada por"
          value={requestedByMe ? "Tú" : (correction.requested_by_name || correction.requested_by)}
        />
        <MetaRow label="Fecha de solicitud" value={formatInstant(correction.requested_at, tz)} />
        {correction.decided_by && (
          <>
            <MetaRow
              label={correction.status === "approved" ? "Aprobada por" : correction.status === "rejected" ? "Rechazada por" : "Cancelada por"}
              value={correction.decided_by === currentUserId ? "Tú" : (correction.decided_by_name || correction.decided_by)}
            />
            <MetaRow label="Fecha de decisión" value={formatInstant(correction.decided_at, tz)} />
          </>
        )}
        {correction.note && (
          <MetaRow
            label="Nota del solicitante"
            value={<span style={{ fontStyle: "italic" }}>"{correction.note}"</span>}
            stack
          />
        )}
        {correction.decision_comment && (
          <MetaRow label="Motivo de la decisión" value={correction.decision_comment} stack />
        )}

        <DiffView correction={correction} tz={tz} />

        {(canDecide || canCancel) && (
          <div style={{
            marginTop: 8,
            padding: 14,
            border: "1px solid var(--kr-border)",
            borderRadius: 8,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
              {canDecide ? (isSelfOrigin ? "Decisión como aprobador" : "Esta corrección afecta a tu jornada — confírmala") : "Cancelar mi solicitud"}
            </div>
            {canDecide && (
              <Field label="Comentario (opcional)">
                <Input
                  type="textarea"
                  rows={2}
                  maxLength={2000}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Motivo, contexto, observación…"
                />
              </Field>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {canDecide && (
                <>
                  <Button variant="dangerSoft" onClick={handleReject} disabled={submitting} icon="X">
                    Rechazar
                  </Button>
                  <Button variant="primary" onClick={handleApprove} disabled={submitting} icon="Check">
                    Aprobar
                  </Button>
                </>
              )}
              {canCancel && !canDecide && (
                <Button variant="dangerSoft" onClick={handleCancel} disabled={submitting} icon="Trash2">
                  Cancelar solicitud
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}

function MetaRow({ label, value, stack }) {
  return (
    <div style={{
      display: stack ? "block" : "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 0",
      borderBottom: "1px solid var(--kr-border-subtle)",
      gap: 8,
    }}>
      <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--kr-fg-strong)", textAlign: stack ? "left" : "right", marginTop: stack ? 4 : 0 }}>
        {value || "—"}
      </div>
    </div>
  );
}

function DiffView({ correction, tz }) {
  const previous = correction.previous_snapshot || { segments: [], breaks: [] };
  const proposed = {
    segments: correction.proposed_segments || [],
    breaks: correction.proposed_breaks || [],
  };

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 8 }}>
        Comparación de marcajes
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <DiffColumn title="Antes" tone="neutral" data={previous} tz={tz} />
        <DiffColumn title="Después (propuesto)" tone="info" data={proposed} tz={tz} />
      </div>
    </div>
  );
}

function DiffColumn({ title, tone, data, tz }) {
  const segments = data?.segments || [];
  const breaks = data?.breaks || [];
  const segMin = segments.reduce((acc, s) => {
    if (!s.clock_in_at || !s.clock_out_at) return acc;
    return acc + Math.max(0, Math.round((new Date(s.clock_out_at) - new Date(s.clock_in_at)) / 60000));
  }, 0);
  const brMin = breaks.reduce((acc, b) => {
    if (!b.start_at || !b.end_at) return acc;
    return acc + Math.max(0, Math.round((new Date(b.end_at) - new Date(b.start_at)) / 60000));
  }, 0);
  const net = Math.max(0, segMin - brMin);

  const borderColor = tone === "info" ? "var(--kr-action)" : "var(--kr-border)";

  return (
    <div style={{
      padding: 12,
      border: `1px solid ${borderColor}`,
      borderRadius: 8,
      background: tone === "info" ? "var(--kr-action-soft-bg)" : "var(--kr-bg-surface)",
    }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 700, marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 17, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
        {Math.floor(net / 60)}h {String(net % 60).padStart(2, "0")}m
      </div>
      <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>
        {segments.length} segmento{segments.length === 1 ? "" : "s"} · {breaks.length} pausa{breaks.length === 1 ? "" : "s"}
      </div>

      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
        {segments.length === 0 && <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontStyle: "italic" }}>Sin segmentos</div>}
        {segments.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Icon name="Play" size={12} style={{ color: "var(--kr-success-700)" }} />
            <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
              {formatTimeHHMM(s.clock_in_at, tz)} – {s.clock_out_at ? formatTimeHHMM(s.clock_out_at, tz) : "abierto"}
            </span>
          </div>
        ))}
        {breaks.map((b, i) => (
          <div key={`b${i}`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Icon name="Coffee" size={12} style={{ color: "var(--kr-warning-700)" }} />
            <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
              {formatTimeHHMM(b.start_at, tz)} – {formatTimeHHMM(b.end_at, tz)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default DetalleCorreccionDrawer;
